import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { HiCheck } from 'react-icons/hi';
import Signature from '../../Form/FormElements/Signature';
import { fetchWrapper } from '@/_helpers';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import DateTimePicker from 'react-datetime-picker/dist/DateTimePicker';
import { css } from 'twin.macro';
import dayjs from 'dayjs';
import { VscLoading } from 'react-icons/vsc';
import OurAccordion from '@/_elements/OurAccordion';
import ImageModal from '@/components/Features/Teams/ImageModal';
import { LoadingSpinner } from '@/_elements/Loaders';

const CompleteDraftVisitModal = ({ visitData, handleUpdateVisitRow = () => {}, team }) => {
    const signatureRef = useRef();
    const [isBusy, setIsBusy] = useState(false);
    const [show, setShow] = useState(false);
    const [isModalReady, setIsModalReady] = useState(false);
    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);
    const [endDate, setEndDate] = useState(null);
    const onChange = (value) => {
        setEndDate(value ? new Date(value) : null);
    };

    const lastSubmittedForm = useMemo(
        () => (visitData.forms?.sort((a, b) => a.createdAt - b.createdAt) || []).at(-1) || {},
        [visitData]
    );

    const handleCompleteVisit = () => {
        const modifyVisit = async () => {
            try {
                setIsBusy(true);
                const signature = signatureRef?.current && new FormData(signatureRef.current).get('signature');
                if (!signature) {
                    return toast.error('Signature is missing');
                }
                const eosFormFound = lastSubmittedForm?.type === 'eos';
                if (!eosFormFound) {
                    if (!endDate) {
                        return toast.error('End Date and Time must be set');
                    }
                    if (
                        dayjs(visitData?.ScheduleStartTime).isSame(dayjs(endDate)) ||
                        dayjs(visitData?.ScheduleStartTime).isAfter(dayjs(endDate))
                    ) {
                        return toast.error('Start Date must be less than End Date.');
                    }
                    if (dayjs(endDate).diff(dayjs(visitData?.ScheduleStartTime), 'hour') > 24) {
                        return toast.error(
                            <div>
                                <b>Ops! Billing hour exceed 24 hours.</b>
                                <br />
                                <br />
                                <span>
                                    Difference of <b>start dateTime</b> and <b>end dateTime</b> must be less than 24hrs
                                </span>
                            </div>,
                            {
                                autoClose: 10000,
                            }
                        );
                    }
                }

                const body = {
                    visit: visitData.id || visitData._id,
                    signature,
                    serviceEndAt: endDate?.toISOString() || undefined,
                };
                const response = await fetchWrapper.patch(`evv/visit/complete?${team ? `&team=${team}` : ''}`, {
                    ...body,
                });
                handleUpdateVisitRow(response);
                toast.success('Visit completed successfully');
            } catch (err) {
                console.log(err);
                toast.error(err.message || 'Something went wrong');
            } finally {
                setIsBusy(false);
            }
        };
        const message =
            visitData?.status === 'draft'
                ? 'Completing a visit will automatically send your information to external EVV systems like Sandata and Therap.'
                : 'Reverting payment status will not update already created checks with this visit.';
        Swal.fire({
            title: 'Are you sure?',
            html: `<div style="font-size:16px;color:brown;text-align:justify;width:100%;">${message}</div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#50aeb0',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                modifyVisit();
            }
        });
    };
    return (
        <Fragment>
            <HiCheck className="icon" onClick={handleShow} />
            <Modal
                show={show}
                centered
                size="xl"
                backdrop="static"
                onEntered={() => setIsModalReady(true)}
                onExit={() => setIsModalReady(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Complete Draft Visit</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ height: '80vh' }}>
                    <div className="ml-4">
                        <strong>Draft Visit Info</strong>
                        <hr />
                        <ul>
                            <li>Provider: {visitData?.provider?.agencyName}</li>
                            <li>
                                Client: {visitData?.client?.firstName} {visitData?.client?.lastName}
                            </li>
                            <li>
                                Employee: {visitData?.employee?.firstName} {visitData?.employee?.lastName}
                            </li>
                            <li>Start Date: {dayjs(visitData.ScheduleStartTime).format('MM-DD-YYYY, h:mm A')}</li>
                            <li>
                                End Date:
                                {lastSubmittedForm?.type === 'eos' ? (
                                    <span className="ml-2">
                                        {dayjs(visitData.ScheduleEndTime).format('MM-DD-YYYY, h:mm A')}
                                    </span>
                                ) : (
                                    <span className="ml-2">-- -- --</span>
                                )}
                            </li>
                        </ul>
                        <hr />
                    </div>
                    <br />
                    {lastSubmittedForm?.type !== 'eos' && (
                        <div>
                            <span className="text-danger mr-2">EOS form not submitted (Enter Service End Date):</span>
                            <DateTimePicker
                                css={css`
                                    display: inline-block;
                                    width: 312px;
                                `}
                                onChange={onChange}
                                minDate={new Date(visitData.ScheduleStartTime)}
                                maxDate={new Date()}
                                value={endDate}
                                format="MM-dd-y hh:mm a"
                                disableClock={true}
                                calendarType={'US'}
                            />
                        </div>
                    )}
                    #Note: EOS Submission Date will be used for "service end date" if found.
                    <br />
                    <br />
                    {isModalReady && (
                        <form ref={signatureRef} onSubmit={(e) => e.preventDefault()}>
                            <Signature name="signature" data={{ required: true }} />
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleCompleteVisit} disabled={isBusy}>
                        {isBusy ? <VscLoading className="spin" /> : <span>Submit</span>}
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default CompleteDraftVisitModal;

export const DraftCompletedInfo = ({ visitData }) => {
    const [file, setFile] = useState({});
    const [isBusy, setIsBusy] = useState(false);
    const fileId = useMemo(() => visitData?.extraInfo?.CompleteDraftVisitSignature, [visitData]);
    useEffect(() => {
        const loadFile = async () => {
            try {
                setIsBusy(true);
                const response = await fetchWrapper.get(`file/${fileId}`);
                setFile(response);
            } catch (e) {
                if (e.name === 'AbortError') {
                    return;
                }
            } finally {
                setIsBusy(false);
            }
        };
        if (fileId) {
            loadFile();
        }
    }, [fileId]);
    if (!fileId) {
        return null;
    }

    return (
        <div
            css={css`
                border: 1px solid #ccc;
                padding: 4px;
            `}
        >
            <OurAccordion title={<strong>Draft Completion Details:</strong>} defaultShow={false}>
                {isBusy ? (
                    <LoadingSpinner />
                ) : (
                    <ul>
                        <li>Submitted By: {file?.user?.name}</li>
                        <li>Submitted At: {dayjs(file?.createdAt).format('MM-DD-YYYY, h:mm A')}</li>
                        <li> Signature:</li>
                        <ImageModal file={file?.url || ''} />
                    </ul>
                )}
            </OurAccordion>
        </div>
    );
};
